import { create } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';

// Single home for persisted per-device UI preferences (theme, view modes,
// selected ranges). All preferences share one localStorage key so they persist
// the same way; add new ones here instead of ad-hoc localStorage reads.
//
// The one deliberate exception is the language preference: it lives in the
// i18next cookie because it must be readable before hydration (see the
// prerender notes in src/i18n.tsx).

export type Theme = 'dark' | 'light' | 'system';
export type AnalyticsPeriod = '7d' | '30d' | '90d';
export type AnalyticsConsent = 'granted' | 'denied';

export const PREFERENCES_STORAGE_KEY = 'ywt-preferences';

// Theme used to live under its own key; seed the initial state from it so
// existing visitors keep their choice. Once they change any preference, the
// value is carried in ywt-preferences (which rehydration prefers over this).
const LEGACY_THEME_KEY = 'vite-ui-theme';

const readLegacyTheme = (): Theme | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(LEGACY_THEME_KEY);
    return stored === 'dark' || stored === 'light' || stored === 'system' ? stored : null;
  } catch {
    return null;
  }
};

// No-op storage for environments without localStorage: the react-router
// prerender (Node, build time), tests, and browsers where storage access
// throws (private mode, blocked storage). Preferences then live in memory only.
const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const safeStorage = (): StateStorage => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch {
    // fall through to noopStorage
  }
  return noopStorage;
};

interface PreferencesStore {
  theme: Theme;
  analyticsPeriod: AnalyticsPeriod;
  // Filters, CSV export and bulk actions on the account domains page. Off by
  // default: most accounts hold a handful of domains and only need the table.
  domainTools: boolean;
  // The visitor's answer to the analytics consent banner; null until they answer
  // (or after they reopen "Cookie settings"), which is what shows the banner.
  analyticsConsent: AnalyticsConsent | null;

  setTheme: (theme: Theme) => void;
  setAnalyticsPeriod: (period: AnalyticsPeriod) => void;
  setDomainTools: (domainTools: boolean) => void;
  setAnalyticsConsent: (analyticsConsent: AnalyticsConsent | null) => void;
}

export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set) => ({
      theme: readLegacyTheme() ?? 'dark',
      analyticsPeriod: '7d',
      domainTools: false,
      analyticsConsent: null,

      setTheme: (theme) => set({ theme }),
      setAnalyticsPeriod: (analyticsPeriod) => set({ analyticsPeriod }),
      setDomainTools: (domainTools) => set({ domainTools }),
      setAnalyticsConsent: (analyticsConsent) => set({ analyticsConsent }),
    }),
    {
      name: PREFERENCES_STORAGE_KEY,
      storage: createJSONStorage(safeStorage),
    },
  ),
);
